import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, ArrowRight,
  // Pack 1 — Papier d'État
  Send, Feather, ThumbsUp, Stamp, BadgeCheck, CheckCircle,
  Pencil, PenLine, HelpCircle, ScrollText, FolderOpen, Archive,
  Bell, Mail, Search,
  // Pack 2 — Marin Antique
  Ship, Compass, Waves, Anchor, LifeBuoy, Telescope, MapPin, Map,
  Calendar, CalendarDays, Activity, Target, Crosshair,
  // Pack 3 — Héraldique
  Star, Crown, ShieldCheck, Award, Feather as Quill, Trophy,
  Sailboat,
} from 'lucide-react';

// ── SVG custom : Sceau de cire (Wax seal) ───────────────────────
const WaxSeal = ({ size = 48, color = '#9A3B2E' }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
    <circle cx="32" cy="32" r="26" fill={color} />
    <circle cx="32" cy="32" r="22" fill={color} stroke="#fff" strokeWidth="0.6" strokeDasharray="2 2" />
    {/* Wax drips */}
    <path d="M14 38 Q10 48 14 56 Q18 50 16 42 Z" fill={color} opacity="0.85" />
    <path d="M50 36 Q56 44 52 54 Q46 48 48 40 Z" fill={color} opacity="0.85" />
    {/* Star center */}
    <path d="M32 18 L35 30 L46 32 L35 34 L32 46 L29 34 L18 32 L29 30 Z" fill="#fff" opacity="0.9" />
    <circle cx="32" cy="32" r="3" fill={color} />
  </svg>
);

// ── SVG custom : Rosace 8 branches (Compass rose) ──────────────
const CompassRose = ({ size = 56, color = '#B5894A' }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
    <circle cx="32" cy="32" r="28" stroke={color} strokeWidth="1" strokeDasharray="2 3" />
    <circle cx="32" cy="32" r="20" stroke={color} strokeWidth="1.2" />
    {/* 8 branches */}
    <path d="M32 8 L34 32 L32 56 L30 32 Z" fill={color} />
    <path d="M8 32 L32 30 L56 32 L32 34 Z" fill={color} opacity="0.55" />
    <path d="M15 15 L32 30 L49 49 L30 32 Z" fill={color} opacity="0.7" transform="rotate(0 32 32)" />
    <path d="M49 15 L32 30 L15 49 L30 32 Z" fill={color} opacity="0.4" />
    <circle cx="32" cy="32" r="3.5" fill="#16233B" />
    <circle cx="32" cy="32" r="1.5" fill={color} />
  </svg>
);

// ── SVG custom : Monogramme EO ─────────────────────────────────
const MonogramEO = ({ size = 48, color = '#B5894A' }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
    <circle cx="32" cy="32" r="28" stroke={color} strokeWidth="1" />
    <text x="32" y="42" textAnchor="middle" fontFamily="Georgia, serif" fontSize="28" fill={color} fontWeight="700" fontStyle="italic">EO</text>
    {/* Ornament dashes */}
    <path d="M14 32 L18 32 M50 32 L46 32" stroke={color} strokeWidth="1" />
  </svg>
);

// ── SVG custom : Plumes croisées ───────────────────────────────
const CrossedFeathers = ({ size = 40, color = '#16233B' }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
    <g transform="rotate(-20 32 32)">
      <path d="M20 12 Q24 32 22 52 L26 52 Q28 32 24 12 Z" fill={color} />
      <path d="M21 18 L27 22 M21 26 L27 30 M21 34 L27 38 M21 42 L27 46" stroke="#fff" strokeWidth="0.7" />
    </g>
    <g transform="rotate(20 32 32)">
      <path d="M40 12 Q44 32 42 52 L46 52 Q48 32 44 12 Z" fill={color} opacity="0.7" />
      <path d="M41 18 L47 22 M41 26 L47 30 M41 34 L47 38 M41 42 L47 46" stroke="#fff" strokeWidth="0.7" />
    </g>
  </svg>
);

// ── SVG custom : Couronne navale ───────────────────────────────
const NavalCrown = ({ size = 56, color = '#B5894A' }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
    {/* Crown base */}
    <path d="M10 38 L54 38 L52 50 L12 50 Z" fill={color} />
    <path d="M10 38 L14 24 L20 32 L26 18 L32 30 L38 18 L44 32 L50 24 L54 38 Z" fill={color} stroke="#8C6635" strokeWidth="0.6" />
    {/* Anchor in center */}
    <g transform="translate(32 42)">
      <line x1="0" y1="-4" x2="0" y2="4" stroke="#16233B" strokeWidth="1.5" />
      <path d="M-4 4 Q0 7 4 4 M-3 2 L3 2" stroke="#16233B" strokeWidth="1.5" fill="none" />
    </g>
    <circle cx="14" cy="24" r="1.5" fill="#8C6635" />
    <circle cx="32" cy="30" r="1.5" fill="#8C6635" />
    <circle cx="50" cy="24" r="1.5" fill="#8C6635" />
  </svg>
);

// ── SVG custom : Médailles bronze/argent/or/diamant ─────────────
const Medal = ({ size = 56, tier = 'gold' }) => {
  const colors = {
    bronze:  { ring: '#A66A3C', face: '#C58858', shine: '#E0B488' },
    silver:  { ring: '#7E8388', face: '#B5BCC2', shine: '#DDE0E3' },
    gold:    { ring: '#8C6635', face: '#B5894A', shine: '#E9DCC4' },
    diamond: { ring: '#3F5A85', face: '#7A95C3', shine: '#DFE7F2' },
  };
  const c = colors[tier] || colors.gold;
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
      {/* Ribbon */}
      <path d="M22 6 L32 24 L42 6 L46 12 L36 30 L28 30 L18 12 Z" fill="#9A3B2E" opacity="0.9" />
      {/* Medal disc */}
      <circle cx="32" cy="42" r="16" fill={c.face} />
      <circle cx="32" cy="42" r="16" fill="none" stroke={c.ring} strokeWidth="2" />
      <circle cx="32" cy="42" r="13" fill="none" stroke={c.shine} strokeWidth="0.5" strokeDasharray="1 1.5" />
      {/* Star */}
      <path d="M32 32 L34 40 L42 40 L36 44 L38 52 L32 47 L26 52 L28 44 L22 40 L30 40 Z" fill={c.shine} />
      <text x="32" y="46" textAnchor="middle" fontFamily="Georgia, serif" fontSize="9" fill={c.ring} fontWeight="700">EO</text>
    </svg>
  );
};

// ── SVG custom : Couronne de lauriers ──────────────────────────
const LaurelWreath = ({ size = 64, color = '#B5894A', children }) => (
  <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
    <svg viewBox="0 0 64 64" width={size} height={size} className="absolute inset-0">
      <path d="M14 32 Q14 14 28 8" stroke={color} strokeWidth="1.5" fill="none" />
      <path d="M50 32 Q50 14 36 8" stroke={color} strokeWidth="1.5" fill="none" />
      <path d="M14 32 Q14 50 28 56" stroke={color} strokeWidth="1.5" fill="none" />
      <path d="M50 32 Q50 50 36 56" stroke={color} strokeWidth="1.5" fill="none" />
      {/* Leaves */}
      {[12,20,28,36,44].map(y => (
        <g key={`l-${y}`}>
          <ellipse cx={16} cy={y} rx="3" ry="1.5" fill={color} transform={`rotate(-30 16 ${y})`} />
          <ellipse cx={48} cy={y} rx="3" ry="1.5" fill={color} transform={`rotate(30 48 ${y})`} />
        </g>
      ))}
    </svg>
    <span className="relative z-10 font-serif font-bold text-ink text-sm">{children}</span>
  </div>
);

// ── Carte de démo ──────────────────────────────────────────────
const IconRow = ({ before, after, label, sub }) => (
  <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-paper-2/40 rounded-lg transition-colors">
    <div className="w-10 h-10 rounded-full bg-paper-2 border border-line flex items-center justify-center flex-shrink-0 opacity-50">
      {before}
    </div>
    <ArrowRight className="w-4 h-4 text-muted-dossier flex-shrink-0" />
    <div className="w-10 h-10 rounded-full bg-gold-bg border border-gold-soft flex items-center justify-center flex-shrink-0">
      {after}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-ink m-0">{label}</p>
      {sub && <p className="text-xs italic text-muted-dossier m-0">{sub}</p>}
    </div>
  </div>
);

const Section = ({ num, title, subtitle, children }) => (
  <section className="dossier-card mb-8" style={{ padding: '1.5rem 1.75rem' }}>
    <p className="dossier-label" style={{ margin: 0 }}>§ {num}</p>
    <h2 className="text-2xl mt-1 mb-1 filet-gold" style={{ fontFamily: 'Georgia, serif' }}>{title}</h2>
    <p className="italic text-sm text-muted-dossier mt-3 mb-4">{subtitle}</p>
    {children}
  </section>
);

const IconsPreviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user || user.role !== 'admin') {
    return <div className="min-h-screen flex items-center justify-center bg-paper"><p>Accès réservé aux admins.</p></div>;
  }

  const inkColor = 'hsl(218 48% 16%)';
  const goldDark = 'hsl(33 45% 38%)';

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="icons-preview-page">
      <header className="border-b border-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="btn-pill-secondary"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <div>
            <h1 className="text-2xl m-0 filet-gold">Iconographie — Aperçu</h1>
            <p className="italic text-sm text-muted-dossier mt-3 m-0">3 packs proposés. Choisis ton style.</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">

        {/* PACK 1 ─ Papier d'État */}
        <Section num="01" title="Pack 1 — Papier d'État" subtitle="Documents officiels : plume calligraphique, sceau de cire, parchemin, encrier. Look notarial / cabinet juridique premium.">
          <div className="grid md:grid-cols-2 gap-2">
            <IconRow before={<Send className="w-5 h-5" color={inkColor} />} after={<Feather className="w-5 h-5" color={goldDark} />} label="Envoyer message" sub="Send → Feather (plume)" />
            <IconRow before={<ThumbsUp className="w-5 h-5" color={inkColor} />} after={<Stamp className="w-5 h-5" color={goldDark} />} label="Réagir / Like" sub="ThumbsUp → Stamp (sceau)" />
            <IconRow before={<CheckCircle className="w-5 h-5" color={inkColor} />} after={<BadgeCheck className="w-5 h-5" color={goldDark} />} label="Confirmer" sub="CheckCircle → BadgeCheck" />
            <IconRow before={<Pencil className="w-5 h-5" color={inkColor} />} after={<PenLine className="w-5 h-5" color={goldDark} />} label="Modifier" sub="Pencil → PenLine (plume + filet)" />
            <IconRow before={<HelpCircle className="w-5 h-5" color={inkColor} />} after={<ScrollText className="w-5 h-5" color={goldDark} />} label="Q&A / Questions" sub="HelpCircle → ScrollText (parchemin)" />
            <IconRow before={<FolderOpen className="w-5 h-5" color={inkColor} />} after={<Archive className="w-5 h-5" color={goldDark} />} label="Ressources" sub="FolderOpen → Archive (classeur)" />
            <IconRow before={<Bell className="w-5 h-5" color={inkColor} />} after={<Mail className="w-5 h-5" color={goldDark} />} label="Notifications" sub="Bell → Mail (pli scellé)" />
            <IconRow before={<Search className="w-5 h-5" color={inkColor} />} after={<Search className="w-5 h-5" color={goldDark} />} label="Recherche" sub="OK — garder la loupe" />
          </div>

          <div className="mt-6 flex items-center gap-6 p-5 bg-paper-2/50 rounded-lg border border-line">
            <p className="dossier-label" style={{ margin: 0 }}>Bonus signature SVG</p>
            <div className="flex items-center gap-5">
              <div className="text-center">
                <WaxSeal size={56} />
                <p className="text-xs italic text-muted-dossier mt-2 mb-0">Sceau de cire</p>
              </div>
              <div className="text-center">
                <MonogramEO size={56} />
                <p className="text-xs italic text-muted-dossier mt-2 mb-0">Monogramme EO</p>
              </div>
              <div className="text-center">
                <CrossedFeathers size={48} />
                <p className="text-xs italic text-muted-dossier mt-2 mb-0">Plumes croisées</p>
              </div>
            </div>
          </div>
        </Section>

        {/* PACK 2 ─ Marin Antique */}
        <Section num="02" title="Pack 2 — Marin Antique" subtitle="Boussole, longue-vue, rosace, carte ancienne. Renforce le branding L'Équipage Odys.">
          <div className="grid md:grid-cols-2 gap-2">
            <IconRow before={<Ship className="w-5 h-5" color={inkColor} />} after={<Compass className="w-5 h-5" color={goldDark} />} label="Mon Odyssée" sub="Ship → Compass (boussole)" />
            <IconRow before={<Waves className="w-5 h-5" color={inkColor} />} after={<Anchor className="w-5 h-5" color={goldDark} />} label="Saisons" sub="Waves → Anchor orné" />
            <IconRow before={<Telescope className="w-5 h-5" color={inkColor} />} after={<Telescope className="w-5 h-5" color={goldDark} />} label="Vigie Immo" sub="OK — garder" />
            <IconRow before={<MapPin className="w-5 h-5" color={inkColor} />} after={<Map className="w-5 h-5" color={goldDark} />} label="Carte membres" sub="MapPin → Map (carte parchemin)" />
            <IconRow before={<Calendar className="w-5 h-5" color={inkColor} />} after={<CalendarDays className="w-5 h-5" color={goldDark} />} label="Calendrier" sub="Calendar → CalendarDays" />
            <IconRow before={<Activity className="w-5 h-5" color={inkColor} />} after={<Waves className="w-5 h-5" color={goldDark} />} label="Activité" sub="Activity → Waves (sillage)" />
            <IconRow before={<Target className="w-5 h-5" color={inkColor} />} after={<Crosshair className="w-5 h-5" color={goldDark} />} label="Objectifs" sub="Target → Crosshair (cap)" />
            <IconRow before={<LifeBuoy className="w-5 h-5" color={inkColor} />} after={<LifeBuoy className="w-5 h-5" color={goldDark} />} label="Aide / Support" sub="LifeBuoy (bouée)" />
          </div>

          <div className="mt-6 flex items-center gap-6 p-5 bg-paper-2/50 rounded-lg border border-line">
            <p className="dossier-label" style={{ margin: 0 }}>Bonus signature SVG</p>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <CompassRose size={64} />
                <p className="text-xs italic text-muted-dossier mt-2 mb-0">Rosace 8 branches</p>
              </div>
              <div className="text-center">
                <NavalCrown size={64} />
                <p className="text-xs italic text-muted-dossier mt-2 mb-0">Couronne navale</p>
              </div>
            </div>
          </div>
        </Section>

        {/* PACK 3 ─ Héraldique Bancaire */}
        <Section num="03" title="Pack 3 — Héraldique Bancaire" subtitle="Médailles gravées, couronnes, lauriers, blasons. Pour la gamification (niveaux, rôles, badges).">

          <p className="dossier-label mb-3" style={{ margin: '0 0 0.75rem' }}>Niveaux (Niv. 1 → 15)</p>
          <div className="flex flex-wrap items-end gap-6 p-5 bg-paper-2/50 rounded-lg border border-line mb-6">
            <div className="text-center">
              <Medal size={64} tier="bronze" />
              <p className="text-xs italic text-muted-dossier mt-2 mb-0">Bronze · Niv. 1-3</p>
              <p className="text-[10px] uppercase tracking-wider text-gold-dark mt-1">Moussaillon</p>
            </div>
            <div className="text-center">
              <Medal size={64} tier="silver" />
              <p className="text-xs italic text-muted-dossier mt-2 mb-0">Argent · Niv. 4-6</p>
              <p className="text-[10px] uppercase tracking-wider text-gold-dark mt-1">Matelot</p>
            </div>
            <div className="text-center">
              <Medal size={64} tier="gold" />
              <p className="text-xs italic text-muted-dossier mt-2 mb-0">Or · Niv. 7-9</p>
              <p className="text-[10px] uppercase tracking-wider text-gold-dark mt-1">Capitaine</p>
            </div>
            <div className="text-center">
              <Medal size={64} tier="diamond" />
              <p className="text-xs italic text-muted-dossier mt-2 mb-0">Diamant · Niv. 10-12</p>
              <p className="text-[10px] uppercase tracking-wider text-gold-dark mt-1">Amiral</p>
            </div>
            <div className="text-center">
              <NavalCrown size={64} />
              <p className="text-xs italic text-muted-dossier mt-2 mb-0">Couronne · Niv. 13-15</p>
              <p className="text-[10px] uppercase tracking-wider text-gold-dark mt-1">Empereur</p>
            </div>
          </div>

          <p className="dossier-label mb-3" style={{ margin: '0 0 0.75rem' }}>Rôles utilisateurs</p>
          <div className="flex flex-wrap items-center gap-3 p-5 bg-paper-2/50 rounded-lg border border-line mb-6">
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-red-dossier/30">
              <ShieldCheck className="w-4 h-4" style={{ color: 'hsl(var(--red-dossier))' }} />
              <span className="text-sm font-semibold text-ink">Admin</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-gold-soft">
              <Award className="w-4 h-4 text-gold" />
              <span className="text-sm font-semibold text-ink">Major</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full" style={{ borderWidth: 1, borderStyle: 'solid', borderColor: 'hsl(var(--teal) / 0.4)' }}>
              <Star className="w-4 h-4" style={{ color: 'hsl(var(--teal-dark))' }} />
              <span className="text-sm font-semibold text-ink">Ambassadeur</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-line">
              <Quill className="w-4 h-4 text-ink" />
              <span className="text-sm font-semibold text-ink">Mentoring</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-line">
              <Sailboat className="w-4 h-4 text-muted-dossier" />
              <span className="text-sm font-semibold text-ink">Start</span>
            </div>
          </div>

          <p className="dossier-label mb-3" style={{ margin: '0 0 0.75rem' }}>Profile frames (avatars)</p>
          <div className="flex flex-wrap items-center gap-6 p-5 bg-paper-2/50 rounded-lg border border-line mb-6">
            {['bronze','silver','gold','diamond'].map(t => (
              <div key={t} className="text-center">
                <LaurelWreath size={80} color={t === 'bronze' ? '#A66A3C' : t === 'silver' ? '#7E8388' : t === 'diamond' ? '#3F5A85' : '#B5894A'}>
                  AO
                </LaurelWreath>
                <p className="text-xs italic text-muted-dossier mt-2 mb-0 capitalize">{t}</p>
              </div>
            ))}
          </div>

          <p className="dossier-label mb-3" style={{ margin: '0 0 0.75rem' }}>Tampons & Badges</p>
          <div className="flex flex-wrap items-center gap-3 p-5 bg-paper-2/50 rounded-lg border border-line">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold-bg border border-gold-soft text-gold-dark text-xs font-bold uppercase tracking-widest">
              <BadgeCheck className="w-3.5 h-3.5" /> Validé
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest" style={{ background: 'hsl(var(--ink))', color: 'hsl(var(--paper))' }}>
              <Stamp className="w-3.5 h-3.5" /> Officiel
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-gold text-gold-dark text-xs font-bold uppercase tracking-widest">
              <Trophy className="w-3.5 h-3.5" /> Premium
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest" style={{ background: 'hsl(var(--red-dossier) / 0.1)', borderWidth: 1, borderStyle: 'solid', borderColor: 'hsl(var(--red-dossier) / 0.3)', color: 'hsl(var(--red-dossier))' }}>
              <Crown className="w-3.5 h-3.5" /> Empereur
            </span>
          </div>
        </Section>

        <footer className="pt-6 mt-8 text-center">
          <p className="italic text-muted-dossier text-sm">
            Combo recommandé : <strong className="text-ink">Pack 1 + Pack 3</strong> (Papier + Héraldique) — garder Anchor/Compass du brand maritime.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default IconsPreviewPage;
